import React, { useState } from "react";
import { Play } from "lucide-react";
import { cn } from "../../../lib/utils";

export type VideoPlatform = "youtube" | "vimeo" | "loom";

interface DocsVideoEmbedProps {
  platform: VideoPlatform;
  videoId: string;
  title?: string;
  className?: string;
}

const platformConfig: Record<
  VideoPlatform,
  { embedUrl: (id: string) => string; thumbnailUrl: (id: string) => string }
> = {
  youtube: {
    embedUrl: (id) => `https://www.youtube.com/embed/${id}`,
    thumbnailUrl: (id) => `https://img.youtube.com/vi/${id}/maxresdefault.jpg`,
  },
  vimeo: {
    embedUrl: (id) => `https://player.vimeo.com/video/${id}`,
    thumbnailUrl: (id) => `https://vumbnail.com/${id}.jpg`,
  },
  loom: {
    embedUrl: (id) => `https://www.loom.com/embed/${id}`,
    thumbnailUrl: (id) =>
      `https://cdn.loom.com/sessions/thumbnails/${id}-00001.png`,
  },
};

export function DocsVideoEmbed({
  platform,
  videoId,
  title,
  className,
}: DocsVideoEmbedProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const config = platformConfig[platform];

  if (isPlaying) {
    return (
      <div className={cn("my-6 relative", className)}>
        <div className="relative w-full pb-[56.25%] bg-[var(--su-bg-secondary)] border border-[var(--su-border-primary)]">
          <iframe
            src={config.embedUrl(videoId)}
            title={title || "Video"}
            className="absolute top-0 left-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={cn("my-6 relative group cursor-pointer", className)}>
      <div
        className="relative w-full pb-[56.25%] bg-[var(--su-bg-secondary)] border border-[var(--su-border-primary)] overflow-hidden"
        onClick={() => setIsPlaying(true)}
      >
        <img
          src={config.thumbnailUrl(videoId)}
          alt={title || "Video thumbnail"}
          className="absolute top-0 left-0 w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            // Fallback to a placeholder if thumbnail fails to load
            e.currentTarget.style.display = "none";
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
          <div className="w-16 h-16 rounded-none bg-[var(--su-purple-accent)] flex items-center justify-center">
            <Play className="w-8 h-8 text-black ml-1" fill="currentColor" />
          </div>
        </div>
        {title && (
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-white font-medium">{title}</p>
          </div>
        )}
      </div>
    </div>
  );
}
